import { useEffect, useState, useCallback } from "react";
import api, { currency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
    const [branches, setBranches] = useState([]);
    const [filters, setFilters] = useState({ branch_id: "all", start_date: "", end_date: "" });
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        (async () => { try { const { data } = await api.get("/branches"); setBranches(data); } catch (e) { /* silent */ } })();
    }, []);

    const buildPayload = () => ({
        branch_id: filters.branch_id === "all" ? null : filters.branch_id,
        start_date: filters.start_date || null,
        end_date: filters.end_date || null,
    });

    const download = async (fmt) => {
        try {
            const res = await api.post(`/reports/portfolio.${fmt}`, buildPayload(), { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Komma-Portfolio-${new Date().toISOString().slice(0, 10)}.${fmt}`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`${fmt.toUpperCase()} report downloaded`);
        } catch { toast.error("Could not generate report"); }
    };

    const loadPreview = useCallback(async () => {
        try {
            const { data } = await api.get("/loans");
            let list = data;
            if (filters.branch_id !== "all") list = list.filter((l) => l.branch_id === filters.branch_id);
            if (filters.start_date) list = list.filter((l) => l.created_at >= filters.start_date);
            if (filters.end_date) list = list.filter((l) => l.created_at <= filters.end_date + "T23:59:59");
            const totals = {
                count: list.length,
                pipeline: list.reduce((s, l) => s + l.amount, 0),
                disbursed: list.filter((l) => l.status === "disbursed").reduce((s, l) => s + l.amount, 0),
                approved: list.filter((l) => ["approved", "disbursed"].includes(l.status)).length,
            };
            setPreview(totals);
        } catch (e) { /* silent */ }
    }, [filters.branch_id, filters.start_date, filters.end_date]);

    useEffect(() => { loadPreview(); }, [loadPreview]);

    return (
        <div className="space-y-6" data-testid="reports-page">
            <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">Board Room</p>
                <h1 className="font-serif text-3xl text-komma-navy">Portfolio Reports</h1>
                <p className="text-sm text-gray-600 mt-1">Branded, board-ready exports filtered by branch and date.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="komma-card p-6 lg:col-span-1">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-komma-navy">Filters</p>
                    <div className="komma-gold-hairline my-4" />
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-komma-navy">Branch</Label>
                            <Select value={filters.branch_id} onValueChange={(v) => setFilters({ ...filters, branch_id: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="report-branch"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All branches</SelectItem>
                                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-komma-navy">From</Label>
                            <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="mt-1.5 h-10" data-testid="report-start" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-komma-navy">To</Label>
                            <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="mt-1.5 h-10" data-testid="report-end" />
                        </div>
                    </div>
                    <div className="komma-gold-hairline my-6" />
                    <div className="space-y-2">
                        <Button onClick={() => download("pdf")} className="w-full bg-komma-navy hover:bg-komma-navy-light" data-testid="report-pdf-btn">
                            <FileText size={14} className="mr-2" /> Download PDF
                        </Button>
                        <Button onClick={() => download("csv")} variant="outline" className="w-full border-komma-gold text-komma-navy hover:bg-komma-gold/10" data-testid="report-csv-btn">
                            <FileSpreadsheet size={14} className="mr-2" /> Download CSV
                        </Button>
                    </div>
                </div>

                <div className="komma-card p-8 lg:col-span-2 relative overflow-hidden">
                    <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-komma-gold/10" />
                    <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">Preview</p>
                    <h2 className="font-serif text-2xl text-komma-navy mt-2">
                        {filters.branch_id === "all" ? "All Branches" : (branches.find((b) => b.id === filters.branch_id)?.name || "Branch")}
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                        {filters.start_date || "beginning"} → {filters.end_date || "today"}
                    </p>
                    <div className="komma-gold-hairline my-6" />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <StatBox label="Loans" value={preview?.count ?? "—"} />
                        <StatBox label="Approved" value={preview?.approved ?? "—"} />
                        <StatBox label="Pipeline" value={preview ? currency(preview.pipeline) : "—"} />
                        <StatBox label="Disbursed" value={preview ? currency(preview.disbursed) : "—"} accent />
                    </div>
                    <p className="mt-8 text-xs text-gray-500 flex items-center gap-2">
                        <Download size={12} className="text-komma-gold" /> PDF is board-ready with the Komma logo, navy/gold theme, and per-loan detail. CSV is finance-friendly for pivot tables.
                    </p>
                </div>
            </div>
        </div>
    );
}

function StatBox({ label, value, accent }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-gray-500">{label}</p>
            <p className={`font-serif text-2xl mt-1 ${accent ? "text-komma-gold-dark" : "text-komma-navy"}`}>{value}</p>
        </div>
    );
}
